import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { EarlyAccessBlockedDialogService } from './early-access-blocked-dialog.service';
import { PointsService } from './points.service';
import { TokenService } from './token.service';
import {
  EarlyAccessBlockedError,
  EarlyAccessGateRejection,
  EarlyAccessRestrictable,
  EarlyAccessViewer,
  earlyAccessThresholdOf,
  evaluateEarlyAccess,
  mapEarlyAccessGate403,
} from '../perron-points/early-access-gate';
import { PERRON_POINTS_COPY } from '../perron-points/perron-points-copy';
import { Product } from '../models/product.model';
import { CART_COPY } from '../../features/checkout/checkout-copy';
import {
  Cart,
  CartEnvelope,
  CartGoneError,
  CartStockError,
  CartTokenRequest,
  NewCartLineRequest,
  Preorder,
  PreorderEnvelope,
  PreorderError,
  PreorderRequest,
  PromoCodeError,
  PromoCodeErrorReason,
  PromoCodeRequest,
  UpdateCartLineRequest,
  isCartGone,
  unwrapCart,
  unwrapPreorder,
} from '../models/cart.model';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly api = inject(ApiService);
  private readonly tokenService = inject(TokenService);
  private readonly authService = inject(AuthService);
  private readonly pointsService = inject(PointsService);
  private readonly blockedDialog = inject(EarlyAccessBlockedDialogService);

  private readonly _cart = signal<Cart | null>(null);

  readonly cart = this._cart.asReadonly();
  readonly cartCount = computed(() => this._cart()?.productsQuantity ?? 0);
  readonly cartToken = computed(() => this._cart()?.token ?? null);

  /**
   * Líneas de Acceso VIP que el cliente no puede comprar ahora mismo, como
   * `id de línea → umbral efectivo` (R4). Se DERIVA en cada lectura del
   * carrito y del estado de sesión/saldo: nada se persiste, no hay jobs de
   * limpieza y una línea no se borra sola. Por eso mismo cubre R6 sin código
   * propio: al cerrar sesión el umbral deja de cumplirse y la línea pasa a
   * pintarse bloqueada, sin diálogo ni aviso especial al salir.
   *
   * Mientras el saldo se está verificando (o si su carga falló) la línea NO se
   * marca: sería un parpadeo de «Bloqueado» en cada recarga para clientes que
   * sí cumplen. Ese hueco lo cubren las puertas del servidor, que devuelven
   * 403 identificando la línea.
   */
  readonly blockedLines = computed<ReadonlyMap<number, number>>(() => {
    const blocked = new Map<number, number>();
    const lines = this._cart()?.lines ?? [];
    if (lines.length === 0) return blocked;

    const viewer = this.earlyAccessViewer();
    const fallback = this.pointsService.displayThreshold();

    for (const line of lines) {
      const decision = evaluateEarlyAccess(
        earlyAccessThresholdOf(line.product, fallback),
        viewer,
      );
      if (!decision.allowed && (decision.reason === 'guest' || decision.reason === 'insufficient')) {
        blocked.set(line.id, decision.threshold);
      }
    }

    return blocked;
  });

  readonly hasBlockedLines = computed(() => this.blockedLines().size > 0);

  /**
   * Aviso de cabecera del carrito y del checkout, en la variante que
   * corresponde: la salida del invitado (iniciar sesión) no es la del cliente
   * con saldo corto (quitar las líneas). `null` si no hay nada bloqueado.
   */
  readonly blockedLinesNotice = computed<string | null>(() => {
    if (!this.hasBlockedLines()) return null;
    return this.authService.isAuthenticated()
      ? PERRON_POINTS_COPY.blockedLine.noticeAuthenticated
      : PERRON_POINTS_COPY.blockedLine.noticeGuest;
  });

  /**
   * Recupera un preorder por su token público. Lo usa la página de éxito
   * cuando se accede vía link directo o tras refresh (no hay router state).
   */
  getPreorderByToken(token: string): Observable<Preorder> {
    return this.api
      .get<PreorderEnvelope>(`/show-preorder`, { token })
      .pipe(map(unwrapPreorder));
  }

  getCart(): Observable<Cart | null> {
    const token = this.tokenService.getCartToken();
    if (!token) {
      this._cart.set(null);
      return of(null);
    }

    return this.api.get<CartEnvelope>(`/get-cart-by-token`, { token }).pipe(
      map((res) => {
        if (typeof res === 'string') return null;
        const cart = unwrapCart(res);
        return cart && typeof cart === 'object' && 'id' in cart ? cart : null;
      }),
      tap((cart) => {
        if (cart) {
          this._cart.set(cart);
        } else {
          this.tokenService.clearCartToken();
          this._cart.set(null);
        }
      }),
      catchError(() => {
        this.tokenService.clearCartToken();
        this._cart.set(null);
        return of(null);
      }),
    );
  }

  /**
   * Agrega una línea al carrito, cortando antes los productos de Acceso VIP
   * que el cliente no puede comprar (R2). El corte vive AQUÍ, no en cada uno de
   * los ocho call sites: las tres vías de compra del CLAUDE.md más el buscador,
   * la home, las landings y el bundle FBT pasan todas por este método.
   *
   * `product` es opcional solo por compatibilidad de firma; sin él no hay nada
   * que evaluar en el cliente y el corte lo hace el servidor con su 403 (que
   * también se mapea aquí). Todos los call sites lo pasan.
   */
  addLine(productId: number, quantity: number, product?: Product | null): Observable<Cart> {
    const blocked = this.earlyAccessBlock(product, product?.name ?? '', productId);
    if (blocked) return throwError(() => blocked);

    const token = this.tokenService.getCartToken();
    const body: NewCartLineRequest = {
      product_id: productId,
      quantity,
      ...(token ? { token } : {}),
    };

    return this.api.post<CartEnvelope>('/new-cart-line', body).pipe(
      map(unwrapCart),
      tap((cart) => {
        if (cart.token && cart.token !== token) {
          this.tokenService.setCartToken(cart.token);
        }
        this._cart.set(cart);
      }),
      catchError((err) => this.handleCartError(err)),
    );
  }

  /**
   * Cambia la cantidad de una línea. El gate solo aplica al SUBIR (R9b): bajar
   * una línea bloqueada se permite siempre, igual que quitarla, porque reducir
   * exposición nunca se bloquea — si no, el cliente se quedaría con una línea
   * que no puede comprar ni encoger.
   */
  updateLine(lineId: number, productId: number, quantity: number): Observable<Cart> {
    const line = this._cart()?.lines.find((l) => l.id === lineId) ?? null;
    if (line && quantity > line.quantity) {
      const blocked = this.earlyAccessBlock(line.product, line.name, productId);
      if (blocked) return throwError(() => blocked);
    }

    const token = this.tokenService.getCartToken();
    if (!token) {
      return throwError(() => new Error('No hay carrito activo'));
    }

    const body: UpdateCartLineRequest = {
      product_id: productId,
      quantity,
      token,
    };

    return this.api.put<CartEnvelope>(`/update-cart-line/${lineId}`, body).pipe(
      map(unwrapCart),
      tap((cart) => this._cart.set(cart)),
      catchError((err) => this.handleCartError(err)),
    );
  }

  removeLine(lineId: number): Observable<Cart | null> {
    const token = this.tokenService.getCartToken();
    if (!token) {
      return throwError(() => new Error('No hay carrito activo'));
    }

    const body: CartTokenRequest = { token };

    return this.api.delete<CartEnvelope>(`/destroy-cart-line/${lineId}`, body).pipe(
      map(unwrapCart),
      tap((cart) => {
        if (!cart || cart.total <= 0 || cart.productsQuantity <= 0) {
          this.tokenService.clearCartToken();
          this._cart.set(null);
        } else {
          this._cart.set(cart);
        }
      }),
      map((cart) => (cart.total <= 0 || cart.productsQuantity <= 0 ? null : cart)),
      catchError((err) => this.handleCartError(err)),
    );
  }

  applyPromoCode(code: string): Observable<Cart> {
    const cart = this._cart();
    if (!cart) {
      return throwError(() => new PromoCodeError('not_applicable', 'No hay carrito activo'));
    }

    const token = this.tokenService.getCartToken();
    if (!token) {
      return throwError(() => new PromoCodeError('cart_expired', CART_COPY.gone));
    }

    const normalized = code.trim().toUpperCase();
    const body: PromoCodeRequest = { code: normalized, cart_id: cart.id, token };

    return this.api.post<unknown>('/validate-promotional-code', body).pipe(
      switchMap(() => this.api.post<CartEnvelope>('/confirm-apply-promotional-code', body)),
      map(unwrapCart),
      tap((updated) => this._cart.set(updated)),
      catchError((err) => this.handlePromoError(err)),
    );
  }

  removePromoCode(): Observable<Cart> {
    const cart = this._cart();
    if (!cart) {
      return throwError(() => new Error('No hay carrito activo'));
    }

    const token = this.tokenService.getCartToken();
    if (!token) {
      return throwError(() => new Error('No hay carrito activo'));
    }

    const body: CartTokenRequest = { token };

    return this.api.delete<CartEnvelope>(`/remove-promotional-code/${cart.id}`, body).pipe(
      map(unwrapCart),
      tap((updated) => this._cart.set(updated)),
      catchError((err) => {
        if (isCartGone(err)) {
          this.clearCart();
          return throwError(() => new CartGoneError(CART_COPY.gone));
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Convierte el cart actual en preorder llamando al backend. Tras éxito, limpia
   * el cart local (signal + token) porque el backend elimina el cart server-side
   * al crear el preorder.
   *
   * Para SINPE/transferencia el comprobante (`proof`) viaja en la MISMA llamada
   * (multipart): el pedido y el comprobante se crean atómicamente — sin
   * comprobante no hay pedido (create-on-finalize). Para `online` no hay `proof`
   * y el payload va como JSON. `clearCart()` solo corre en el `tap` de éxito.
   */
  cartToPreorder(
    payload: PreorderRequest,
    cartId: number,
    proof?: File,
  ): Observable<Preorder> {
    // La API exige el token del carrito en el body (nunca en query); `toMultipart` lo serializa tal cual.
    const token = this.tokenService.getCartToken();
    const withToken: PreorderRequest = token ? { ...payload, token } : payload;
    const body = proof ? this.toMultipart(withToken, proof) : withToken;
    return this.api
      .post<PreorderEnvelope>(`/cart-to-preorder/${cartId}`, body)
      .pipe(
        map(unwrapPreorder),
        tap(() => this.clearCart()),
        catchError((err) => this.handlePreorderError(err)),
      );
  }

  /**
   * Serializa el payload + comprobante a `FormData`. Omite `null`/`undefined` (el
   * backend los trata como ausentes, no como el string "null") y manda los
   * booleanos como `'1'`/`'0'`: la regla `boolean` de Laravel rechaza
   * `"true"`/`"false"` en multipart.
   */
  private toMultipart(payload: PreorderRequest, proof: File): FormData {
    const fd = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      fd.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    });
    fd.append('image', proof);
    return fd;
  }

  /**
   * Limpia el cart local: signal a null + borra token de localStorage.
   * Lo invoca `cartToPreorder` tras éxito y se expone público por si otros
   * flujos lo necesitan en el futuro.
   */
  clearCart(): void {
    this.tokenService.clearCartToken();
    this._cart.set(null);
  }

  /**
   * Aplana el cuerpo de error de la API a un string legible + la lista de campos.
   * El backend devuelve los 422 de validación como `{ error: { campo: [msgs] } }`
   * (objeto), no como string: hacer `.toString()` sobre ese objeto produce
   * `'[object Object]'`. Aquí distinguimos string vs objeto y concatenamos los
   * mensajes; `fields` permite mapear errores concretos (p. ej. `image`).
   */
  private flattenApiError(body: unknown): { text: string; fields: string[] } {
    if (!body || typeof body !== 'object') return { text: '', fields: [] };
    const raw =
      (body as Record<string, unknown>)['error'] ?? (body as Record<string, unknown>)['message'];
    if (typeof raw === 'string') return { text: raw, fields: [] };
    if (raw && typeof raw === 'object') {
      const fields = Object.keys(raw as Record<string, unknown>);
      const text = Object.values(raw as Record<string, unknown>)
        .flat()
        .filter((m): m is string => typeof m === 'string')
        .join(' ');
      return { text, fields };
    }
    return { text: '', fields: [] };
  }

  private handlePreorderError(err: unknown): Observable<never> {
    // 403 de la puerta legacy de `cart-to-preorder` (R12): sin él caería en el
    // genérico «No pudimos procesar tu pedido», que no dice qué línea falla.
    const gate = mapEarlyAccessGate403(err);
    if (gate) {
      if (gate.threshold !== null) this.pointsService.recordThreshold(gate.threshold);
      return throwError(() => new EarlyAccessGateRejection(gate));
    }

    if (err instanceof HttpErrorResponse) {
      // 409 UNIFORME de la puerta de stock (secuestro por sesión de pago): el cuerpo distingue el
      // caso por `error` (stock_held / stock_out / stock_insufficient). El componente enruta el copy
      // por `reason`. Un 409 desconocido (p. ej. out_of_stock_refunded del flujo tarjeta) cae a `stock`.
      if (err.status === 409) {
        const code = (err.error?.error ?? '').toString();
        const message = (err.error?.message ?? '').toString();
        if (code === 'stock_held' || code === 'stock_out' || code === 'stock_insufficient') {
          return throwError(
            () => new PreorderError(code, message || 'No pudimos reservar tu pedido.'),
          );
        }
        return throwError(
          () => new PreorderError('stock', message || 'No pudimos procesar tu pedido.'),
        );
      }

      if (err.status === 422) {
        const { text, fields } = this.flattenApiError(err.error);
        const lower = text.toLowerCase();

        if (fields.includes('image')) {
          return throwError(
            () =>
              new PreorderError(
                'validation',
                'El comprobante debe ser una imagen válida (JPG, PNG, WebP o AVIF) de máximo 5 MB. Revísalo e inténtalo de nuevo.',
              ),
          );
        }
        if (lower.includes('unidades suficientes') || lower.includes('quedan')) {
          return throwError(() => new PreorderError('stock', text));
        }
        if (lower.includes('por cliente') || lower.includes('máximo')) {
          return throwError(() => new PreorderError('max_per_client', text));
        }
        if (lower.includes('código promocional') || lower.includes('promotional')) {
          return throwError(() => new PreorderError('promo', text));
        }
        return throwError(
          () => new PreorderError('validation', 'Revisa los datos del pedido e inténtalo de nuevo.'),
        );
      }

      if (err.status === 413) {
        return throwError(
          () =>
            new PreorderError(
              'validation',
              'La imagen es demasiado grande. Sube una más ligera (máx. 5 MB).',
            ),
        );
      }

      if (err.status >= 500 || err.status === 0) {
        return throwError(
          () => new PreorderError('network', 'Error de conexión. Inténtalo de nuevo.'),
        );
      }
    }

    return throwError(
      () => new PreorderError('unknown', 'No pudimos procesar tu pedido. Inténtalo de nuevo.'),
    );
  }

  /**
   * Evalúa el gate de Acceso VIP para un producto y, si bloquea, ABRE el
   * diálogo y devuelve el error con el que cortar. `null` = adelante.
   *
   * Único punto donde el front decide sobre la restricción al agregar; el
   * servidor sigue siendo la autoridad y su 403 se mapea en `handleCartError`.
   */
  private earlyAccessBlock(
    product: EarlyAccessRestrictable | null | undefined,
    productName: string,
    productId: number,
  ): EarlyAccessBlockedError | null {
    const threshold = earlyAccessThresholdOf(product, this.pointsService.displayThreshold());
    const decision = evaluateEarlyAccess(threshold, this.earlyAccessViewer());

    if (decision.allowed) return null;

    this.blockedDialog.open({ productId, productName, threshold: decision.threshold });
    return new EarlyAccessBlockedError(decision.threshold, decision.reason);
  }

  /** Estado del visitante frente al gate, tal y como lo ve el front. */
  private earlyAccessViewer(): EarlyAccessViewer {
    return {
      authenticated: this.authService.isAuthenticated(),
      balance: this.pointsService.balance(),
      // `threshold()` no nulo ⟺ `/perron-points/me` ya resolvió.
      balanceKnown: this.pointsService.threshold() !== null,
      balanceError: this.pointsService.loadError(),
    };
  }

  private handleCartError(err: unknown): Observable<never> {
    // Carrito ajeno o inexistente (IDOR, tramo 1): mismo desenlace que `getCart`
    // ante un token que ya no vale — token fuera, signal a null.
    if (isCartGone(err)) {
      this.clearCart();
      return throwError(() => new CartGoneError(CART_COPY.gone));
    }
    if (err instanceof HttpErrorResponse && err.status === 417) {
      const message = err.error?.error ?? 'No hay suficientes unidades disponibles';
      return throwError(() => new CartStockError(message));
    }
    // 403 de `new-cart-line` / `update-cart-line`: red de seguridad del
    // servidor cuando el cliente no pudo evaluar (payload sin umbral, saldo
    // cambiado en otra pestaña…). Se mapea con la línea identificada.
    const gate = mapEarlyAccessGate403(err);
    if (gate) {
      if (gate.threshold !== null) this.pointsService.recordThreshold(gate.threshold);
      return throwError(() => new EarlyAccessGateRejection(gate));
    }
    return throwError(() => err);
  }

  private handlePromoError(err: unknown): Observable<never> {
    // Va PRIMERO: «No exist-e ningún carrito…» casa con `raw.includes('exist')`
    // y se leería como código inválido.
    if (isCartGone(err)) {
      this.clearCart();
      return throwError(() => new PromoCodeError('cart_expired', CART_COPY.gone));
    }
    if (err instanceof HttpErrorResponse) {
      const raw = (err.error?.error ?? '').toString().toLowerCase();
      let reason: PromoCodeErrorReason = 'unknown';
      if (raw.includes('válid') || raw.includes('exist')) reason = 'invalid';
      else if (raw.includes('expir') || raw.includes('vencid')) reason = 'expired';
      else if (raw.includes('mínimo') || raw.includes('minim')) reason = 'min_amount';
      else if (raw.includes('aplica')) reason = 'not_applicable';
      return throwError(
        () => new PromoCodeError(reason, err.error?.error ?? 'Código no aplicable'),
      );
    }
    return throwError(() => err);
  }
}
